import { ComponentConfigType } from '../../utils/data-parser';
import { ComponentType, type IConfig } from '../../types';
import {
  BASIC_VALUE_TOOLTIP,
  BASIC_SELECT_VALUE_TOOLTIP,
  BASIC_JSON_LOGIC_TOOLTIP,
  BASIC_VARIABLE_TOOLTIP,
} from '../../utils';

const VALUE = [{ key: ComponentConfigType.Value, value: BASIC_VALUE_TOOLTIP }];
const BOOL_OPTIONS = [
  { label: 'True', value: true },
  { label: 'False', value: false },
];
const VAR_VALUE_LOGIC = [
  ComponentConfigType.Variable,
  ComponentConfigType.Value,
  ComponentConfigType.JsonLogic,
];
const VAR_VALUE_LOGIC_TOOLTIPS = [
  { key: ComponentConfigType.Variable, value: BASIC_VARIABLE_TOOLTIP },
  { key: ComponentConfigType.Value, value: BASIC_SELECT_VALUE_TOOLTIP },
  { key: ComponentConfigType.JsonLogic, value: BASIC_JSON_LOGIC_TOOLTIP },
];

/**
 * Read-only flow-chart PREVIEW: generated from the document via `deriveConfig`
 * (resources → sections → slots + chain + rules + groups). Node/panel content
 * and role visuals are all configurable.
 */
export const config: IConfig = {
  component: ComponentType.FlowChart,
  configGroups: [
    {
      title: 'Mapping',
      items: [
        {
          name: 'deriveConfig',
          label:
            'Derive config (JSON: operationsPath, chain, rules, groups, resources → sections → slots)',
          valueComponent: {
            type: 'CodeEditor',
            placeholder:
              '{ "operationsPath": "manufacturingBusinessOperations", "chain": [...], "rules": [...], "groups": {...}, "resources": [...] }',
          },
          configTypes: [ComponentConfigType.Value],
          tooltips: VALUE,
        },
        {
          name: 'nodeGroups',
          label: 'Role visuals (JSON: [{ key, label, color, icon }])',
          valueComponent: {
            type: 'CodeEditor',
            placeholder:
              '[{ "key": "as-mfg", "label": "Active substance", "color": "#9a6c2c", "icon": "ActiveSubstanceIcon" }]',
          },
          configTypes: [ComponentConfigType.Value],
          tooltips: VALUE,
        },
      ],
    },
    {
      title: 'Node card',
      items: [
        {
          name: 'nodeCardSchema',
          label: 'Node card (display schema — $node / $row scope)',
          valueComponent: {
            type: 'SubSchemaEditor',
            props: { schemaName: 'nodeCardSchema', buttonText: 'Edit node card' },
          },
          configTypes: [],
        },
      ],
    },
    {
      title: 'Properties panel',
      items: [
        {
          name: 'panelSchema',
          label: 'Panel (display schema — $node / $row / $sides scope)',
          valueComponent: {
            type: 'SubSchemaEditor',
            props: { schemaName: 'panelSchema', buttonText: 'Edit panel' },
          },
          configTypes: [],
        },
        {
          name: 'panelWidth',
          label: 'Panel width (px, default 360)',
          valueComponent: { type: 'InputNumber', placeholder: '360' },
          configTypes: [ComponentConfigType.Value],
          tooltips: VALUE,
        },
      ],
    },
    {
      title: 'Display mode',
      items: [
        {
          name: 'openInModal',
          label: 'Open in modal (button + near-fullscreen modal instead of inline)',
          valueComponent: { type: 'Switch' },
          configTypes: [ComponentConfigType.Value],
          tooltips: VALUE,
        },
        {
          name: 'modalTitle',
          label: 'Modal title',
          valueComponent: { type: 'Input', placeholder: 'Manufacturing flow chart' },
          configTypes: [ComponentConfigType.Value],
          tooltips: VALUE,
        },
        {
          name: 'height',
          label: 'Canvas height (px, inline mode; default 600)',
          valueComponent: { type: 'InputNumber', placeholder: '600' },
          configTypes: [ComponentConfigType.Value],
          tooltips: VALUE,
        },
      ],
    },
    {
      title: 'Trigger button (when Open in modal)',
      items: [
        {
          name: 'triggerButtonText',
          label: 'Text',
          valueComponent: { type: 'Input', placeholder: 'Open flow chart' },
          configTypes: VAR_VALUE_LOGIC,
          tooltips: VAR_VALUE_LOGIC_TOOLTIPS,
        },
        {
          name: 'triggerButtonType',
          label: 'Type',
          valueComponent: {
            type: 'Select',
            options: [
              { label: 'Primary', value: 'primary' },
              { label: 'Default', value: 'default' },
              { label: 'Link', value: 'link' },
              { label: 'Text', value: 'text' },
              { label: 'Dashed', value: 'dashed' },
            ],
            defaultValue: 'primary',
          },
          configTypes: VAR_VALUE_LOGIC,
          tooltips: VAR_VALUE_LOGIC_TOOLTIPS,
        },
        {
          name: 'triggerButtonSize',
          label: 'Size',
          valueComponent: {
            type: 'Select',
            options: [
              { label: 'Small', value: 'small' },
              { label: 'Middle', value: 'middle' },
              { label: 'Large', value: 'large' },
            ],
            defaultValue: 'middle',
          },
          configTypes: VAR_VALUE_LOGIC,
          tooltips: VAR_VALUE_LOGIC_TOOLTIPS,
        },
        {
          name: 'triggerButtonBlock',
          label: 'Block',
          valueComponent: {
            type: 'Select',
            options: BOOL_OPTIONS,
            defaultValue: false,
          },
          configTypes: VAR_VALUE_LOGIC,
          tooltips: VAR_VALUE_LOGIC_TOOLTIPS,
        },
        {
          name: 'triggerButtonAlign',
          label: 'Align',
          valueComponent: {
            type: 'Select',
            options: [
              { label: 'Left', value: 'left' },
              { label: 'Center', value: 'center' },
              { label: 'Right', value: 'right' },
            ],
            defaultValue: 'left',
          },
          configTypes: VAR_VALUE_LOGIC,
          tooltips: VAR_VALUE_LOGIC_TOOLTIPS,
        },
      ],
    },
  ],
};
